import React, { ChangeEvent, MouseEvent, useMemo, useState } from "react";

import {
  ArrayUtils,
  getUserFacingApiErrorMessage,
  isFieldEnrolled,
  useAddFieldPracticeParticipations,
  useGetAllFieldPracticesByFarm,
  useGetFarmReportingYear,
  useGetFarms,
  useTranslation,
} from "utils";

import { AddFieldPracticeParticipationsRequest, FieldListField, PRACTICE_SLUG } from "models";

import { Button, Drawer, ExtraInfo, Flex, InputSearch, Link, Tooltip, Typography, Message as message } from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { FieldListCard } from "pages/app/fields/components/FieldListCard";

type FieldEnrollmentDrawerProps = {
  fields: FieldListField[];
  visible: boolean;
  onClose: () => void;
};

export const FieldEnrollmentDrawer = ({ fields, visible, onClose }: FieldEnrollmentDrawerProps) => {
  const { t } = useTranslation("allFieldView");

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"all" | "unenrolled">("unenrolled");
  const [showLearnMoreDrawer, setShowLearnMoreDrawer] = useState(false);

  const { currentFarm } = useGetFarms();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isDataSubmitted, isDataSubmissionReopened } = useSubmissionStatus(activeFarmReportingYear);

  const hectareLimitAsSafeNumber = Math.round(currentFarm?.hectareLimit ?? 0);

  const { mutateAsync: addFieldPracticeParticipationsAsync } = useAddFieldPracticeParticipations(selectedFields, currentFarm?.id);

  const handleOnClose = () => {
    setSelectedFields([]);
    setSearchTerm("");
    setActiveTab("unenrolled");
    setLoading(false);
    onClose();
  };

  const handleEnrollFields = async () => {
    setLoading(true);
    // Enroll fields with CROPS, SEEDINGS, TILLAGES, and ORGANIC_FERTILIZERS
    const practiceToEnroll = [PRACTICE_SLUG.CROPS, PRACTICE_SLUG.SEEDINGS, PRACTICE_SLUG.TILLAGES, PRACTICE_SLUG.ORGANIC_FERTILIZERS];

    try {
      const addFieldPracticeParticipationsRequest: AddFieldPracticeParticipationsRequest[] = selectedFields.map((fieldId) => ({
        fieldId,
        practiceSlugs: practiceToEnroll,
      }));
      const response = await addFieldPracticeParticipationsAsync(addFieldPracticeParticipationsRequest);

      if (response.success) {
        message({ content: t("fieldEnrollmentDrawer.successEnrollingField"), icon: "tick" });
      } else {
        message({ content: t("fieldEnrollmentDrawer.errorEnrollingField"), icon: "error" });
      }

      handleOnClose();
    } catch (error) {
      // The hectare-limit reason (with its numbers) arrives as a coded API error.
      message({ content: getUserFacingApiErrorMessage(error, t("allFieldView:submission.error")), icon: "error" });
      // eslint-disable-next-line no-console
      console.error("Submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSelect = (e: MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedFields.includes(id)) {
      const fieldsToSelect = selectedFields.filter((fieldId) => fieldId !== id);
      setSelectedFields(fieldsToSelect);
    } else {
      setSelectedFields([...selectedFields, id]);
    }
  };

  const handleSearchTermChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target?.value);
  };

  const [enrolledFields, unenrolledFields] = ArrayUtils.partition(fields, (field) => {
    const practiceBundle = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
    return isFieldEnrolled(practiceBundle, currentFarm?.carbonProgram ?? null);
  });

  const totalEnrolledHa = enrolledFields.reduce((sum, field) => sum + (field.size || 0), 0);

  const fieldFilter = (field: FieldListField) => {
    if (!searchTerm) return true;
    return field.name && field.name.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const displayFields = activeTab === "all" ? fields.filter(fieldFilter) : unenrolledFields.filter(fieldFilter);

  // Calculate the total ha of selected fields
  const selectedHa = useMemo(
    () => displayFields.filter((field) => selectedFields.includes(field.id)).reduce((sum, field) => sum + (field.size || 0), 0),
    [displayFields, selectedFields]
  );

  const isHaLimitExceeded = totalEnrolledHa + selectedHa > hectareLimitAsSafeNumber;

  return (
    <>
      <Drawer
        className="field-enrollment-drawer"
        footer={
          <Button
            block
            disabled={loading || isHaLimitExceeded || (isDataSubmitted && !isDataSubmissionReopened) || !selectedFields.length}
            loading={loading}
            onClick={handleEnrollFields}
            type="primary"
          >
            {t("fieldEnrollmentDrawer.enrollInProgram")}
          </Button>
        }
        title={t("fieldEnrollmentDrawer.title")}
        visible={visible}
        onClose={handleOnClose}
      >
        <Typography variant="h2" className="margin-bottom-100">
          {t("fieldEnrollmentDrawer.heading")}
        </Typography>

        <Typography variant="paragraph-m" className="margin-bottom-100">
          {t("fieldEnrollmentDrawer.description")}{" "}
        </Typography>

        <Link
          dataTestId="field-enrollment-drawer-learn-more"
          className="margin-bottom-200"
          type="primary-link"
          onClick={() => setShowLearnMoreDrawer((prevState) => !prevState)}
        >
          {t("fieldEnrollmentDrawer.learnMore")}
        </Link>

        <div className="margin-bottom-150">
          <InputSearch value={searchTerm} label={t("fieldEnrollmentDrawer.search")} onChange={handleSearchTermChange} />
        </div>

        <Flex className="enrollment-tabs margin-bottom-125" align="center" justify="space-between">
          <Flex gap="1.5rem">
            <Typography variant="paragraph-m" className={activeTab === "all" ? "tab-button active" : "tab-button"} onClick={() => setActiveTab("all")}>
              {t("fieldEnrollmentDrawer.showAll")}
            </Typography>
            <Typography
              variant="paragraph-m"
              className={activeTab === "unenrolled" ? "tab-button active" : "tab-button"}
              onClick={() => setActiveTab("unenrolled")}
            >
              {t("fieldEnrollmentDrawer.unenrolled", { count: unenrolledFields.length })}
            </Typography>
          </Flex>
          <Tooltip
            title={
              isHaLimitExceeded
                ? t("fieldEnrollmentDrawer.tooltip.limitReached")
                : t("fieldEnrollmentDrawer.tooltip.hectaresAvailable", { hectares: hectareLimitAsSafeNumber })
            }
          >
            <Typography variant="paragraph-m" className="ha-remaining">
              {isHaLimitExceeded
                ? t("fieldEnrollmentDrawer.haTotal", { selected: (totalEnrolledHa + selectedHa).toFixed(1), maxHa: hectareLimitAsSafeNumber })
                : t("fieldEnrollmentDrawer.haActive", {
                    selected: (totalEnrolledHa + selectedHa).toFixed(1),
                    maxHa: hectareLimitAsSafeNumber,
                  })}
              {isHaLimitExceeded && <Typography variant="paragraph-m"> &bull; {t("fieldEnrollmentDrawer.haLimitReachedShort")}</Typography>}
            </Typography>
          </Tooltip>
        </Flex>

        <div className="margin-bottom-100">
          {displayFields.map((field, index) => {
            const isEnrolled = isFieldEnrolled(allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [], currentFarm?.carbonProgram ?? null);
            return (
              <div className="field-cardWithoutMethod margin-bottom-100" key={index}>
                <FieldListCard
                  disabled={isEnrolled}
                  field={field}
                  isSelected={selectedFields.includes(field.id)}
                  onSelectionChange={(e) => handleFieldSelect(e, field.id)}
                />
              </div>
            );
          })}
        </div>
      </Drawer>
      <ExtraInfo setVisible={setShowLearnMoreDrawer} type="enroll-fields" visible={showLearnMoreDrawer} />
    </>
  );
};
